import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  getAdminVideoTags,
  searchAdminTags,
  setAdminVideoTags,
  toAdminApiErrorMessage,
} from '@/api/adminVideos';

/**
 * Lambda 側の ADMIN_TAG_SEARCH_MIN_LENGTH と揃える。
 * `布` `手` のような1文字のタグを引けるようにするため 1。
 */
const TAG_SEARCH_MIN_LENGTH = 1;
/** 入力が落ち着いてから検索する。1文字ごとに投げない。 */
const TAG_SEARCH_DEBOUNCE_MS = 300;
/** Lambda 側の ADMIN_VIDEO_TAG_MAX_COUNT と揃える。 */
const VIDEO_TAG_MAX_COUNT = 50;

function sortedIdList(tags) {
  return tags
    .map((tag) => tag.id)
    .sort((a, b) => a - b)
    .join(',');
}

/**
 * 動画1本のタグ構成を編集するモーダル。
 *
 * 追加・削除はこの中の状態だけを変え、「保存」までDBへは書かない。
 * 保存は「保存後にあるべきタグの全量」を送り、差分の INSERT / DELETE は Lambda 側が行う。
 */
export default function AdminVideoTagEditDialog({ video, open, onOpenChange }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const videoId = video?.id ?? null;

  const [selectedTags, setSelectedTags] = useState([]);
  const [isSeeded, setIsSeeded] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);

  const currentTagsQuery = useQuery({
    queryKey: ['admin-video-tags', videoId],
    queryFn: () => getAdminVideoTags(videoId),
    enabled: Boolean(open && videoId),
    // 編集中に裏で取り直して、入力途中の選択を巻き戻さないようにする
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // 取得結果を編集状態の初期値にするのは、開いてから1回だけ
  useEffect(() => {
    if (!open || isSeeded || !currentTagsQuery.data) return;
    setSelectedTags(currentTagsQuery.data);
    setIsSeeded(true);
  }, [open, isSeeded, currentTagsQuery.data]);

  useEffect(() => {
    if (open) return;
    setIsSeeded(false);
    setSelectedTags([]);
    setKeyword('');
    setDebouncedKeyword('');
    setErrorMessage(null);
  }, [open]);

  useEffect(() => {
    const trimmed = keyword.trim();
    const timer = setTimeout(() => setDebouncedKeyword(trimmed), TAG_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [keyword]);

  const canSearch = debouncedKeyword.length >= TAG_SEARCH_MIN_LENGTH;

  const searchQuery = useQuery({
    queryKey: ['admin-tag-search', debouncedKeyword],
    queryFn: () => searchAdminTags(debouncedKeyword),
    enabled: Boolean(open && canSearch),
  });

  const selectedIds = useMemo(() => new Set(selectedTags.map((tag) => tag.id)), [selectedTags]);

  // 選択済みは候補から外す。同じタグを二重に付けられないようにする。
  const candidates = useMemo(
    () => (searchQuery.data ?? []).filter((tag) => !selectedIds.has(tag.id)),
    [searchQuery.data, selectedIds]
  );

  const savedIdList = sortedIdList(currentTagsQuery.data ?? []);
  const selectedIdList = sortedIdList(selectedTags);
  const hasChanges = savedIdList !== selectedIdList;

  const saveMutation = useMutation({
    mutationFn: (tagIds) => setAdminVideoTags(videoId, tagIds),
    onSuccess: async (result) => {
      setErrorMessage(null);
      // 一覧のタグ表示も取り直してから閉じる
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-videos'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-video-tags', videoId] }),
      ]);
      toast({
        description:
          result.added === 0 && result.removed === 0
            ? 'タグの変更はありませんでした'
            : `タグを更新しました（追加 ${result.added} / 削除 ${result.removed}）`,
      });
      onOpenChange(false);
    },
    onError: (error) => {
      // 編集内容を捨てないよう、モーダルは開いたままにする
      const message = toAdminApiErrorMessage(error, 'タグの更新に失敗しました。');
      setErrorMessage(message);
      toast({ variant: 'destructive', description: message });
    },
  });

  const addTag = (tag) => {
    if (selectedIds.has(tag.id)) return;
    if (selectedTags.length >= VIDEO_TAG_MAX_COUNT) {
      setErrorMessage(`タグは1本あたり${VIDEO_TAG_MAX_COUNT}件までです。`);
      return;
    }
    setErrorMessage(null);
    setSelectedTags((current) => [...current, tag]);
  };

  const removeTag = (tagId) => {
    setErrorMessage(null);
    setSelectedTags((current) => current.filter((tag) => tag.id !== tagId));
  };

  const handleSave = () => {
    if (saveMutation.isPending) return;
    setErrorMessage(null);
    saveMutation.mutate(selectedTags.map((tag) => tag.id));
  };

  const handleOpenChange = (nextOpen) => {
    // 保存中に閉じられると結果が分からなくなるため待たせる
    if (saveMutation.isPending) return;
    onOpenChange(nextOpen);
  };

  const trimmedKeyword = keyword.trim();
  // 空入力のときだけ案内を出す（全件は出さない方針のため）
  const searchHint = trimmedKeyword ? null : 'キーワードを入力すると候補が出ます。';
  // 入力は足りているがデバウンス待ちの間も「検索中」にする
  const isSearching = !canSearch || searchQuery.isLoading;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg border-[#e8dccd] bg-[#faf6f1]">
        <DialogHeader>
          <DialogTitle className="text-[#2d1810]">タグ編集</DialogTitle>
          <DialogDescription className="text-[#6b5540]">
            {video?.title || '(タイトル未設定)'}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <div
            role="alert"
            className="rounded-xl border border-[#d98a7a] bg-[#fdf1ee] px-4 py-3 text-sm text-[#8a3b28]"
          >
            {errorMessage}
          </div>
        )}

        <div>
          <p className="mb-2 text-sm font-medium text-[#2d1810]">
            付与するタグ（{selectedTags.length}件）
          </p>

          {currentTagsQuery.isLoading ? (
            <p className="text-sm text-[#6b5540]">読み込み中...</p>
          ) : currentTagsQuery.isError ? (
            <p className="text-sm text-[#8a3b28]">
              {toAdminApiErrorMessage(currentTagsQuery.error, '現在のタグを取得できませんでした。')}
            </p>
          ) : selectedTags.length === 0 ? (
            <p className="text-sm text-[#6b5540]">タグが付与されていません。</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {selectedTags.map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#d4c4b0] bg-white px-3 py-1 text-xs text-[#5d4529]"
                >
                  {tag.name}
                  {/* 無効化済みでも既存の紐付けは自動で外さない。状態だけ分かるようにする */}
                  {!tag.isActive && (
                    <Badge variant="outline" className="border-[#d98a7a] text-[10px] text-[#8a3b28]">
                      無効
                    </Badge>
                  )}
                  <button
                    type="button"
                    onClick={() => removeTag(tag.id)}
                    disabled={saveMutation.isPending}
                    aria-label={`${tag.name} を外す`}
                    className="text-[#8b6f47] transition-colors hover:text-[#2d1810] disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="admin-tag-search"
            className="mb-2 block text-sm font-medium text-[#2d1810]"
          >
            タグを検索して追加
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b6f47]" />
            <Input
              id="admin-tag-search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="タグ名 / slug で部分一致（1文字から）"
              disabled={saveMutation.isPending}
              className="bg-white pl-9 text-[#2d1810]"
            />
          </div>

          <div className="mt-2 max-h-52 overflow-y-auto rounded-xl border border-[#e8dccd] bg-white">
            {searchHint ? (
              <p className="px-3 py-3 text-xs text-[#6b5540]">{searchHint}</p>
            ) : isSearching ? (
              <p className="flex items-center gap-2 px-3 py-3 text-xs text-[#6b5540]">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#8b6f47]" />
                検索中...
              </p>
            ) : searchQuery.isError ? (
              <p className="px-3 py-3 text-xs text-[#8a3b28]">
                {toAdminApiErrorMessage(searchQuery.error, 'タグ候補を取得できませんでした。')}
              </p>
            ) : candidates.length === 0 ? (
              <p className="px-3 py-3 text-xs text-[#6b5540]">
                該当するタグがありません。（付与済みのタグは候補に出ません）
              </p>
            ) : (
              <ul className="divide-y divide-[#f0e6d8]">
                {candidates.map((tag) => (
                  <li key={tag.id}>
                    <button
                      type="button"
                      onClick={() => addTag(tag)}
                      disabled={saveMutation.isPending}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-[#2d1810] transition-colors hover:bg-[#f5ede3] disabled:opacity-50"
                    >
                      <span className="min-w-0 truncate">{tag.name}</span>
                      {tag.slug && (
                        <span className="flex-shrink-0 text-xs text-[#8b6f47]">{tag.slug}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={saveMutation.isPending}
            className="border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
          >
            キャンセル
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saveMutation.isPending || currentTagsQuery.isLoading || !hasChanges}
            className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#7a6040]"
          >
            {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
